import m from "mithril";
import i18n from "../i18n/i18n";

// Stateful dropdown component for dropdownsingle / dropdownmulti form fields.
// Selection is tracked via option.selected on each option (consistent with checkbox/radio).
// Search is always available.
//
// Attrs:
//   field    - the form field ({ type: "dropdownsingle" | "dropdownmulti", options: [{ id, value, selected }] })
//   readOnly - when true options cannot be changed. The menu can still be opened so a
//              reviewer can see which options are selected (e.g. when the multi-select
//              trigger collapses its selection to an "N selected" summary).
//   onSelect - optional callback invoked after a selection changes
function DropdownField() {
    let open = false;
    let query = "";
    // When the comma-separated list of selected multi-values is too wide for the trigger
    // we fall back to an "N selected" summary. This is measured from the DOM after render.
    let labelOverflows = false;
    // Menu is rendered position:fixed so it escapes the (clipped, scrollable) modal.
    // Coordinates are computed from the trigger's bounding rect whenever the menu opens
    // and while it stays open (scroll / resize).
    let menuStyle = null;

    const MAX_MENU_HEIGHT = 320;

    function positionMenu(rootDom) {
        if (!open || !rootDom) {
            return;
        }
        const trigger = rootDom.querySelector(".dropdown-trigger");
        if (!trigger) {
            return;
        }
        const rect = trigger.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // Flip above the trigger when there isn't room below and there's more room above.
        const flipUp = spaceBelow < MAX_MENU_HEIGHT && spaceAbove > spaceBelow;
        const available = Math.max(120, (flipUp ? spaceAbove : spaceBelow) - 8);
        const maxHeight = Math.min(MAX_MENU_HEIGHT, available);

        let style = `position: fixed; left: ${rect.left}px; width: ${rect.width}px; z-index: 1100; max-height: ${maxHeight}px; overflow-y: auto;`;
        if (flipUp) {
            style += ` bottom: ${window.innerHeight - rect.top}px;`;
        } else {
            style += ` top: ${rect.bottom}px;`;
        }
        menuStyle = style;
    }

    function isMulti(field) {
        return field.type === "dropdownmulti";
    }

    function selectedOptions(field) {
        return field.options.filter(o => o.selected);
    }

    function selectedValuesList(field) {
        return selectedOptions(field).map(o => o.value).join(", ");
    }

    function triggerLabel(field) {
        const selected = selectedOptions(field);
        if (selected.length === 0) {
            if (isMulti(field)) {
                return i18n.t("select_many");
            }
            return i18n.t("select_one");
        }
        if (isMulti(field)) {
            // Prefer the full comma-separated list; collapse to a count only when the list
            // is too wide to fit the trigger (measured against a hidden sizer, see below).
            return labelOverflows ? `${selected.length} ${i18n.t("selected")}` : selectedValuesList(field);
        }
        return selected[0].value;
    }

    // Recompute whether the full comma-separated list fits the trigger, and redraw if the
    // decision changed. A hidden sizer span (.dropdown-label-sizer) always holds the full
    // list at its natural width; comparing that against the label's available width lets us
    // recover (re-expand) when the trigger grows or the selection shrinks - not just collapse.
    function measureLabelOverflow(field, labelDom) {
        const isCollapsibleMulti = isMulti(field) && selectedOptions(field).length > 1;
        if (!isCollapsibleMulti) {
            if (labelOverflows) {
                labelOverflows = false;
                m.redraw();
            }
            return;
        }
        const sizer = labelDom.querySelector(".dropdown-label-sizer");
        if (!sizer) {
            return;
        }
        const overflows = sizer.scrollWidth > labelDom.clientWidth;
        if (overflows !== labelOverflows) {
            labelOverflows = overflows;
            m.redraw();
        }
    }

    function filteredOptions(field) {
        if (!query) {
            return field.options;
        }
        const q = query.toLowerCase();
        return field.options.filter(o => (o.value || "").toLowerCase().includes(q));
    }

    function selectSingle(field, option, markDirty) {
        field.options.forEach(o => o.selected = false);
        option.selected = true;
        open = false;
        query = "";
        markDirty();
    }

    function toggleMulti(option, markDirty) {
        option.selected = !option.selected;
        markDirty();
    }

    return {
        view: function (vnode) {
            const field = vnode.attrs.field;
            const readOnly = vnode.attrs.readOnly;
            const markDirty = vnode.attrs.onSelect || function () {};
            const multi = isMulti(field);

            return m(".form-input dropdown auto-width dropdown-field", {
                class: open ? "open" : "",
                oncreate: function (v) {
                    v.state.onOutsideClick = function (e) {
                        if (!v.dom.contains(e.target)) {
                            open = false;
                            m.redraw();
                        }
                    };
                    // Reposition the fixed menu as the modal (or window) scrolls / resizes.
                    v.state.onReposition = function () {
                        if (open) {
                            positionMenu(v.dom);
                            m.redraw();
                        }
                    };
                    document.addEventListener("click", v.state.onOutsideClick);
                    window.addEventListener("resize", v.state.onReposition);
                    // Capture phase so we also catch scrolling of the modal's inner container.
                    window.addEventListener("scroll", v.state.onReposition, true);
                },
                onremove: function (v) {
                    document.removeEventListener("click", v.state.onOutsideClick);
                    window.removeEventListener("resize", v.state.onReposition);
                    window.removeEventListener("scroll", v.state.onReposition, true);
                }
            }, [
                m("button[type=button].dropdown-trigger", {
                    // Not disabled even when readOnly: a reviewer must still be able to open
                    // the menu to see which options are selected
                    class: selectedOptions(field).length === 0 ? "dropdown-placeholder" : "",
                    onclick: function (e) {
                        open = !open;
                        if (open) {
                            positionMenu(e.currentTarget.closest(".dropdown-field"));
                        }
                    }
                }, [
                    m("span.dropdown-trigger-label", {
                        // Re-measure after every render: selection, resize and font load can all
                        // change whether the full list fits.
                        oncreate: function (v) { measureLabelOverflow(field, v.dom); },
                        onupdate: function (v) { measureLabelOverflow(field, v.dom); }
                    }, [
                        triggerLabel(field),
                        // Hidden sizer holds the full list at natural width so we can tell
                        // whether it would fit even while the visible label shows the summary.
                        (multi && selectedOptions(field).length > 1)
                            ? m("span.dropdown-label-sizer", { "aria-hidden": "true" }, selectedValuesList(field))
                            : ""
                    ]),
                    m("span.dropdown-chevron", { "aria-hidden": "true" })
                ]),
                m(".dropdown-list", {
                    style: (open && menuStyle) ? menuStyle : ""
                }, [
                    m("input[type=text].dropdown-search", {
                        value: query,
                        placeholder: i18n.t("search"),
                        // Keep the menu open while interacting with the search input.
                        onclick: function (e) { e.stopPropagation(); },
                        oninput: function () {
                            query = this.value;
                        }
                    }),
                    filteredOptions(field).map(function (option) {
                        return m("div", {
                            key: option.id,
                            class: (option.selected ? "active" : "") + (readOnly ? " read-only" : ""),
                            onclick: function (e) {
                                // In review mode the menu is view-only: options can be seen but
                                // not changed.
                                if (readOnly) {
                                    e.stopPropagation();
                                    return;
                                }
                                if (multi) {
                                    e.stopPropagation();
                                }
                                multi ? toggleMulti(option, markDirty) : selectSingle(field, option, markDirty);
                            }
                        }, [
                            multi ? m("i", { class: option.selected ? "icon-checkbox-checked mr-1" : "icon-checkbox-unchecked mr-1" }) : "",
                            m("span", option.value)
                        ]);
                    })
                ])
            ]);
        }
    };
}

export default DropdownField;
